import Cookies from 'js-cookie'

const APP_ID = 'p2p'
const COOKIE_EXPIRES_DAYS = 7
const LANGUAGE_KEY = 'languagePreference'
const THEME_KEY = 'themePreference'
const VALUATION_KEY = 'valuationPreference'
const SUPPORTED_LANGUAGES = new Set([
  'en', 'zh-TC', 'zh-CN', 'ru', 'ko', 'ja', 'vi', 'fa', 'uk', 'es', 'fr', 'pt'
])

function isRecord(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function getCookieDomain() {
  if (typeof window === 'undefined') return undefined
  const hostname = window.location.hostname
  if (!hostname || hostname === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
    return undefined
  }
  const parts = hostname.split('.').filter(Boolean)
  if (parts.length < 2) return undefined
  return `.${parts.slice(-2).join('.')}`
}

function getCookieAttrs() {
  const attrs = { expires: COOKIE_EXPIRES_DAYS, path: '/', sameSite: 'Lax' }
  const domain = getCookieDomain()
  if (domain) attrs.domain = domain
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') attrs.secure = true
  return attrs
}

function readCookieValues(name) {
  if (typeof document === 'undefined') return []
  const prefix = `${name}=`
  return document.cookie
    .split(';')
    .map(item => item.trim())
    .filter(item => item.startsWith(prefix))
    .map(item => {
      const value = item.slice(prefix.length)
      try {
        return decodeURIComponent(value)
      } catch (e) {
        return value
      }
    })
}

function readLastCookie(name) {
  const values = readCookieValues(name)
  return values.length ? values[values.length - 1] : ''
}

function writeCookie(name, value) {
  Cookies.remove(name, { path: '/' })
  Cookies.set(name, String(value), getCookieAttrs())
}

function readLatestJsonPreference(name, normalize) {
  let latest = null
  readCookieValues(name).forEach(raw => {
    try {
      const normalized = normalize(JSON.parse(raw))
      if (normalized && (!latest || normalized.updatedAt > latest.updatedAt)) latest = normalized
    } catch (e) {
      // Ignore malformed historical cookies.
    }
  })
  return latest
}

function writeJsonPreference(name, value) {
  writeCookie(name, JSON.stringify(value))
  return value
}

function normalizeLanguage(value) {
  const raw = String(value || '').trim().replace('_', '-')
  if (!raw) return ''
  if (raw === 'kr') return 'ko'
  if (raw === 'zh') return 'zh-CN'
  if (['zh-HK', 'zh-Hk', 'zh-TW'].includes(raw)) return 'zh-TC'
  const exact = Array.from(SUPPORTED_LANGUAGES).find(code => code.toLowerCase() === raw.toLowerCase())
  return exact || ''
}

function normalizeTheme(value) {
  if (value === 'light' || value === 'sunlight') return 'light'
  if (value === 'dark' || value === 'moon') return 'dark'
  return ''
}

function toLocalTheme(value) {
  return normalizeTheme(value) === 'dark' ? 'moon' : 'sunlight'
}

function normalizeLanguagePreference(value) {
  if (!isRecord(value)) return null
  const language = normalizeLanguage(value.language)
  const updatedAt = Number(value.updatedAt)
  if (!language || !Number.isFinite(updatedAt) || updatedAt <= 0) return null
  return { language, updatedAt, source: String(value.source || 'legacy') }
}

function normalizeThemePreference(value) {
  if (!isRecord(value)) return null
  const theme = normalizeTheme(value.theme)
  const updatedAt = Number(value.updatedAt)
  if (!theme || !Number.isFinite(updatedAt) || updatedAt <= 0) return null
  return { theme, updatedAt, source: String(value.source || 'legacy') }
}

function normalizeValuationPreference(value) {
  if (!isRecord(value)) return null
  const currency = String(value.currency || '').trim().toLowerCase()
  const currencyIcon = String(value.currencyIcon || '').trim()
  const updatedAt = Number(value.updatedAt)
  if (!currency || !currencyIcon || !Number.isFinite(updatedAt) || updatedAt <= 0) return null
  return { currency, currencyIcon, updatedAt, source: String(value.source || 'legacy') }
}

function readLanguagePreference() {
  return readLatestJsonPreference(LANGUAGE_KEY, normalizeLanguagePreference)
}

function readThemePreference() {
  return readLatestJsonPreference(THEME_KEY, normalizeThemePreference)
}

function readValuationPreference() {
  return readLatestJsonPreference(VALUATION_KEY, normalizeValuationPreference)
}

function publishLanguage(language) {
  const normalized = normalizeLanguage(language)
  if (!normalized) return null
  const preference = writeJsonPreference(LANGUAGE_KEY, {
    language: normalized,
    updatedAt: Date.now(),
    source: APP_ID
  })
  writeCookie('language', normalized)
  writeCookie('i18n_redirected', normalized)
  return preference
}

function publishTheme(theme) {
  const normalized = normalizeTheme(theme)
  if (!normalized) return null
  const preference = writeJsonPreference(THEME_KEY, {
    theme: normalized,
    updatedAt: Date.now(),
    source: APP_ID
  })
  writeCookie('styleType', toLocalTheme(normalized))
  return preference
}

function publishValuation(currency, currencyIcon) {
  const normalizedCurrency = String(currency || '').trim().toLowerCase()
  const normalizedIcon = String(currencyIcon || '').trim()
  if (!normalizedCurrency || !normalizedIcon) return null
  const preference = writeJsonPreference(VALUATION_KEY, {
    currency: normalizedCurrency,
    currencyIcon: normalizedIcon,
    updatedAt: Date.now(),
    source: APP_ID
  })
  writeCookie('currency', normalizedCurrency)
  writeCookie('currencyIcon', normalizedIcon)
  return preference
}

export function bootstrapCrossProjectPreferences() {
  let language = readLanguagePreference()
  if (language) {
    writeCookie('language', language.language)
    writeCookie('i18n_redirected', language.language)
  } else {
    language = publishLanguage(readLastCookie('language') || readLastCookie('i18n_redirected'))
  }

  let theme = readThemePreference()
  if (theme) {
    writeCookie('styleType', toLocalTheme(theme.theme))
    document.documentElement.setAttribute('data-theme', toLocalTheme(theme.theme))
  } else {
    theme = publishTheme(readLastCookie('styleType'))
  }

  let valuation = readValuationPreference()
  if (valuation) {
    writeCookie('currency', valuation.currency)
    writeCookie('currencyIcon', valuation.currencyIcon)
  } else {
    valuation = publishValuation(readLastCookie('currency') || 'usd', readLastCookie('currencyIcon') || '$')
  }

  return { language, theme, valuation }
}

export function initCrossProjectPreferences(store, i18n) {
  const initial = bootstrapCrossProjectPreferences()
  let applyingSharedState = false
  let valuationScheduled = false
  let lastLanguageUpdatedAt = initial.language ? initial.language.updatedAt : 0
  let lastThemeUpdatedAt = initial.theme ? initial.theme.updatedAt : 0
  let lastValuationUpdatedAt = initial.valuation ? initial.valuation.updatedAt : 0

  const applyLatestSharedState = () => {
    const language = readLanguagePreference()
    const theme = readThemePreference()
    const valuation = readValuationPreference()

    applyingSharedState = true
    try {
      if (language && language.updatedAt > lastLanguageUpdatedAt) {
        lastLanguageUpdatedAt = language.updatedAt
        writeCookie('language', language.language)
        writeCookie('i18n_redirected', language.language)
        store.commit('SET_LANGUAGE', language.language)
        if (i18n && i18n.locale !== language.language) i18n.locale = language.language
        document.documentElement.setAttribute('lang', language.language)
        document.documentElement.setAttribute('dir', language.language === 'fa' ? 'rtl' : 'ltr')
      }

      if (theme && theme.updatedAt > lastThemeUpdatedAt) {
        lastThemeUpdatedAt = theme.updatedAt
        const localTheme = toLocalTheme(theme.theme)
        writeCookie('styleType', localTheme)
        store.commit('SET_STYLE_TYPE', localTheme)
        document.documentElement.setAttribute('data-theme', localTheme)
      }

      if (valuation && valuation.updatedAt > lastValuationUpdatedAt) {
        lastValuationUpdatedAt = valuation.updatedAt
        writeCookie('currency', valuation.currency)
        writeCookie('currencyIcon', valuation.currencyIcon)
        store.commit('SET_CURRENCY', valuation.currency)
        store.commit('SET_CURRENCYICON', valuation.currencyIcon)
      }
    } finally {
      applyingSharedState = false
    }
  }

  const publishCurrentValuation = () => {
    valuationScheduled = false
    if (applyingSharedState) return
    const preference = publishValuation(store.state.user.currency, store.state.user.currencyIcon)
    if (preference) lastValuationUpdatedAt = preference.updatedAt
  }

  const unsubscribe = store.subscribe(mutation => {
    if (applyingSharedState) return

    if (mutation.type === 'SET_STYLE_TYPE') {
      const preference = publishTheme(store.state.user.styleType)
      if (preference) lastThemeUpdatedAt = preference.updatedAt
      return
    }

    if (mutation.type === 'SET_LANGUAGE') {
      const preference = publishLanguage(store.state.user.language)
      if (preference) lastLanguageUpdatedAt = preference.updatedAt
      return
    }

    if (mutation.type === 'SET_CURRENCY' || mutation.type === 'SET_CURRENCYICON') {
      if (valuationScheduled) return
      valuationScheduled = true
      Promise.resolve().then(publishCurrentValuation)
    }
  })

  const flushCompatibilityCookies = () => {
    const rawLanguage = normalizeLanguage(readLastCookie('language'))
    const currentLanguage = readLanguagePreference()
    if (rawLanguage && (!currentLanguage || rawLanguage !== currentLanguage.language)) {
      const preference = publishLanguage(rawLanguage)
      if (preference) lastLanguageUpdatedAt = preference.updatedAt
    }

    const rawTheme = normalizeTheme(readLastCookie('styleType'))
    const currentTheme = readThemePreference()
    if (rawTheme && (!currentTheme || rawTheme !== currentTheme.theme)) {
      const preference = publishTheme(rawTheme)
      if (preference) lastThemeUpdatedAt = preference.updatedAt
    }

    const rawCurrency = String(readLastCookie('currency') || '').trim().toLowerCase()
    const rawIcon = String(readLastCookie('currencyIcon') || '').trim()
    const currentValuation = readValuationPreference()
    if (
      rawCurrency && rawIcon &&
      (!currentValuation || rawCurrency !== currentValuation.currency || rawIcon !== currentValuation.currencyIcon)
    ) {
      const preference = publishValuation(rawCurrency, rawIcon)
      if (preference) lastValuationUpdatedAt = preference.updatedAt
    }
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') flushCompatibilityCookies()
    else applyLatestSharedState()
  }

  window.addEventListener('focus', applyLatestSharedState)
  window.addEventListener('pagehide', flushCompatibilityCookies)
  window.addEventListener('beforeunload', flushCompatibilityCookies)
  document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    unsubscribe()
    window.removeEventListener('focus', applyLatestSharedState)
    window.removeEventListener('pagehide', flushCompatibilityCookies)
    window.removeEventListener('beforeunload', flushCompatibilityCookies)
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}

bootstrapCrossProjectPreferences()
